import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";
import { toVND } from "../../../helpers/helpers";
import ConfirmModal from "../../../Components/Common/ConfirmModal";
import {
    completeDraftOrder,
    deleteDraftOrder,
    fetchDraftOrderDetail,
    removeDraftOrderItem,
    updateDraftOrderItem,
    getOrderDetail,
} from "../../../slices/thunks";
import { DEFAULT_PRODUCT_IMAGE } from "../../../constants/images";
import { AppDispatch } from "../../../store";
import { showAlertWithTimeout } from "../../../slices/Alert/reducer";
import { upsertDraftOrderDetail } from "../../../slices/draftOrderDetails/reducer";

interface DraftOrderDetailProps {
    order: any;
}

const getItemId = (item: any): string | number | undefined =>
    item?.id ?? item?.itemId ?? item?.item_id ?? item?.menu_item_id;

const getOptionLabels = (rawOptions: any): string[] => {
    if (!rawOptions) {
        return [];
    }

    const normalizeValue = (value: any): string[] => {
        if (!value) {
            return [];
        }
        if (Array.isArray(value)) {
            return value.flatMap((v) => normalizeValue(v));
        }
        if (typeof value === "object") {
            if (value.name) {
                return [value.name];
            }
            if (value.label) {
                return [value.label];
            }
            if (value.value && typeof value.value !== "object") {
                return [String(value.value)];
            }
            return Object.values(value).flatMap((v) => normalizeValue(v));
        }
        return [String(value)];
    };

    if (Array.isArray(rawOptions)) {
        return rawOptions.flatMap((opt) => normalizeValue(opt));
    }
    if (typeof rawOptions === "object") {
        return Object.values(rawOptions).flatMap((opt) => normalizeValue(opt));
    }
    return [String(rawOptions)];
};

const DraftOrderDetail: React.FC<DraftOrderDetailProps> = ({ order }) => {
    const { orderId } = useParams<{ orderId: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const [showDeleteOrder, setShowDeleteOrder] = useState(false);
    const [showPrepareOrder, setShowPrepareOrder] = useState(false);
    const [modalSmall, setModalSmall] = useState(false);
    const [pendingItemId, setPendingItemId] = useState<string | number | null>(null);
    const [pendingItemAction, setPendingItemAction] = useState<"update" | "remove" | null>(null);
    const [processingOrder, setProcessingOrder] = useState(false);

    const draftDetails = useSelector((state: any) => state.draftOrderDetails);
    const detailKey = orderId ? String(orderId) : "";
    const detail = detailKey ? draftDetails?.data?.[detailKey] : null;
    const detailLoading = detailKey ? draftDetails?.loading?.[detailKey] : false;

    useEffect(() => {
        if (orderId) {
            dispatch(fetchDraftOrderDetail(orderId));
        }
    }, [dispatch, orderId]);

    useEffect(() => {
        const stateData: any = location.state;
        if (orderId && stateData?.draftOrderDetail) {
            dispatch(upsertDraftOrderDetail({ orderId, data: stateData.draftOrderDetail }));
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [dispatch, location, navigate, orderId]);

    const items = useMemo(() => detail?.items || [], [detail]);

    const totalQuantity = useMemo(
        () =>
            items.reduce(
                (sum: number, item: any) => sum + (item.quantity ?? item.qty ?? 0),
                0,
            ),
        [items],
    );

    const orderTotal = detail?.total ?? order?.total ?? 0;

    const toggleModalSmall = () => {
        setModalSmall(!modalSmall);
    };

    const handleUpdateQuantity = async (item: any, quantity: number) => {
        if (!orderId) {
            return;
        }
        const itemId = getItemId(item);
        if (!itemId) {
            return;
        }
        if (quantity < 1) {
            await handleRemoveItem(item);
            return;
        }
        setPendingItemId(itemId);
        setPendingItemAction("update");
        try {
            await dispatch(
                updateDraftOrderItem({ orderId, itemId, data: { quantity } }),
            ).unwrap();
        } catch (error: any) {
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Không thể cập nhật",
                    error?.toString() || "Vui lòng thử lại sau",
                ),
            );
        } finally {
            setPendingItemId(null);
            setPendingItemAction(null);
        }
    };

    const handleRemoveItem = async (item: any) => {
        if (!orderId) {
            return;
        }
        const itemId = getItemId(item);
        if (!itemId) {
            return;
        }
        setPendingItemId(itemId);
        setPendingItemAction("remove");
        try {
            await dispatch(removeDraftOrderItem({ orderId, itemId })).unwrap();
        } catch (error: any) {
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Không thể xóa món",
                    error?.toString() || "Vui lòng thử lại sau",
                ),
            );
        } finally {
            setPendingItemId(null);
            setPendingItemAction(null);
        }
    };

    const handleDeleteOrder = async () => {
        if (!orderId) {
            return;
        }
        setProcessingOrder(true);
        try {
            await dispatch(deleteDraftOrder(orderId)).unwrap();
            dispatch(
                showAlertWithTimeout(
                    "success",
                    "Đã xóa đơn",
                    "Đơn nháp đã được xóa thành công.",
                ),
            );
            navigate("/order");
        } catch (error: any) {
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Không thể xóa đơn",
                    error?.toString() || "Vui lòng thử lại sau",
                ),
            );
        } finally {
            setProcessingOrder(false);
        }
    };

    const handlePrepareOrder = async () => {
        if (!orderId) {
            return;
        }
        setProcessingOrder(true);
        try {
            await dispatch(completeDraftOrder(orderId)).unwrap();
            await dispatch(getOrderDetail(orderId));
            dispatch(
                showAlertWithTimeout(
                    "success",
                    "Đã chốt đơn",
                    "Đơn đã được chuyển sang trạng thái chuẩn bị.",
                ),
            );
        } catch (error: any) {
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Không thể chốt đơn",
                    error?.toString() || "Vui lòng thử lại sau",
                ),
            );
        } finally {
            setProcessingOrder(false);
        }
    };

    return (
        <React.Fragment>
            <Modal
                size="sm"
                isOpen={modalSmall}
                toggle={toggleModalSmall}
                centered
            >
                <ModalBody>
                    <h6 className="fs-15">Vui lòng nhập số bàn bạn đang ngồi</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="number"
                            id="cardholder"
                            placeholder="VD: 68"
                        />
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={toggleModalSmall} disabled={processingOrder}>
                        Hủy
                    </Button>
                    <Button color="primary" disabled={processingOrder}>
                        Xác nhận
                    </Button>
                </div>
            </Modal>
            <ConfirmModal
                isOpen={showDeleteOrder}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa đơn này không?"
                onConfirm={() => {
                    setShowDeleteOrder(false);
                    handleDeleteOrder();
                }}
                onCancel={() => {
                    setShowDeleteOrder(false);
                }}
            />
            <ConfirmModal
                isOpen={showPrepareOrder}
                typeConfirm="warning"
                title="Chuẩn bị món"
                content="Đơn chưa thanh toán, vẫn xử lý món luôn?"
                onConfirm={() => {
                    setShowPrepareOrder(false);
                    handlePrepareOrder();
                }}
                onCancel={() => {
                    setShowPrepareOrder(false);
                }}
            />
            <div className="cart-wrapper-area py-3">
                {detailLoading && items.length === 0 ? (
                    <p className="text-center py-5">Đang tải danh sách món...</p>
                ) : items.length === 0 ? (
                    <div className="text-center py-5">
                        <p className="mb-3">Đơn hàng chưa có món</p>
                        <Link className="btn btn-primary" to="/order">
                            Quay lại danh sách đơn
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="cart-table card mb-3">
                            <div className="table-responsive card-body">
                                <table className="table mb-0">
                                    <tbody>
                                        {items.map((item: any, i: number) => {
                                            const itemId = getItemId(item);
                                            const productName =
                                                item.productName || item.product_name || item.name;
                                            const price = item.price ?? item.unit_price ?? 0;
                                            const quantity = item.quantity ?? item.qty ?? 0;
                                            const image =
                                                item.imageUrl || item.image_url || DEFAULT_PRODUCT_IMAGE;
                                            const optionLabels = getOptionLabels(item.option ?? item.options);
                                            const disableActions =
                                                pendingItemId !== null && pendingItemId === itemId;

                                            return (
                                                <tr key={i}>
                                                    <td>
                                                        <img className="rounded" src={image} alt="" />
                                                    </td>
                                                    <td>
                                                        <Link className="product-title" to="#">
                                                            {productName}
                                                            <span className="mt-1">{toVND(price)}</span>
                                                        </Link>
                                                        {optionLabels.length > 0 && (
                                                            <ul className="mb-1 ps-3">
                                                                {optionLabels.map((label, idx) => (
                                                                    <li key={idx} className="small text-muted">
                                                                        {label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {item.note && (
                                                            <small className="text-muted">{item.note}</small>
                                                        )}
                                                    </td>
                                                    <td className="align-middle" style={{ width: "160px" }}>
                                                        <div className="btn-group btn-group-sm" role="group">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                disabled={disableActions || quantity <= 1}
                                                                onClick={() =>
                                                                    handleUpdateQuantity(item, quantity - 1)
                                                                }
                                                            >
                                                                –
                                                            </button>
                                                            <span className="btn btn-light disabled">{quantity}</span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                disabled={disableActions}
                                                                onClick={() =>
                                                                    handleUpdateQuantity(item, quantity + 1)
                                                                }
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="align-middle text-end">
                                                        <div className="d-flex align-items-center justify-content-end gap-2">
                                                            <span className="fw-bold">
                                                                {toVND(price * quantity)}
                                                            </span>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                disabled={
                                                                    disableActions &&
                                                                    pendingItemAction === "remove"
                                                                }
                                                                onClick={() => handleRemoveItem(item)}
                                                            >
                                                                <i className="mdi mdi-delete"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="row py-3 g-3">
                            <div className="col-6 col-md-3">
                                <Link
                                    className="btn btn-success w-100"
                                    to="/#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toggleModalSmall();
                                    }}
                                >
                                    Thanh toán
                                </Link>
                            </div>
                            <div className="col-6 col-md-3">
                                <button
                                    className="btn btn-warning w-100"
                                    onClick={() => setShowPrepareOrder(true)}
                                    disabled={processingOrder}
                                >
                                    Chốt đơn
                                </button>
                            </div>
                            <div className="col-6 col-md-3">
                                {orderId ? (
                                    <Link className="btn btn-primary w-100" to={`/order/${orderId}/add-item`}>
                                        Thêm món
                                    </Link>
                                ) : (
                                    <button className="btn btn-primary w-100" disabled>
                                        Thêm món
                                    </button>
                                )}
                            </div>
                            <div className="col-6 col-md-3">
                                <button
                                    className="btn btn-danger w-100"
                                    onClick={() => setShowDeleteOrder(true)}
                                    disabled={processingOrder}
                                >
                                    Xóa đơn
                                </button>
                            </div>
                        </div>
                        <div className="card cart-amount-area">
                            <div className="card-body d-flex align-items-center justify-content-between">
                                <h5 className="total-price mb-0">
                                    {toVND(orderTotal)} ({totalQuantity} món)
                                </h5>
                                <Link className="btn btn-primary" to="/checkout">
                                    Thanh toán
                                </Link>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </React.Fragment>
    );
};

export default DraftOrderDetail;
